// AsistenteReel.jsx
// El "vídeo" 16:9 del Asistente dentro de la landing. No es un vídeo: es la
// pantalla real de la app (screens/Asistente.js + components/CoachIAChat.js)
// replicada en CSS y animada por pasos. Así el texto se lee nítido en cualquier
// pantalla, no pesa nada y un cambio de copy no obliga a regrabar nada.
//
// Arranca solo cuando entra en pantalla y se reproduce una vez. Los números son
// los de Lucía, los MISMOS que usa <ChatWayo /> (1.850 kcal y 130 g de objetivo,
// 1.420 y 88 consumidos): si tocas uno aquí, tócalo también allí.
import React, { useEffect, useRef, useState } from "react";
import "./AsistenteReel.css"; // el plató: fondo, rótulos, barra de avance
import "./AsistenteReelApp.css"; // la pantalla de la app, copiada de su StyleSheet

const GREETING =
  "Buenas tardes, Lucía 👋 Soy Wayo, tu entrenador y nutricionista personal.\n\n" +
  "Conozco tu objetivo, tus macros y lo que llevas comido hoy.";

// La conversación entera. Las cuentas tienen que cuadrar entre sí: 1.850 kcal y
// 130 g de objetivo, 1.420 y 88 consumidas → quedan 430 kcal y faltan 42 g.
const MENSAJES = {
  saludo: { autor: "wayo", texto: GREETING },

  p1: { autor: "usuario", texto: "¿Qué me falta hoy para mis macros?" },
  r1: {
    autor: "wayo",
    texto:
      "Llevas 1.420 kcal de 1.850 y 88 g de proteína de 130.\n\n" +
      "Te quedan 430 kcal y te faltan 42 g de proteína. La cena tiene que tirar " +
      "de proteína y poco más.",
  },

  p2: { autor: "usuario", texto: "¿Y qué ceno para llegar?" },
  r2: {
    autor: "wayo",
    texto:
      "Salmón al horno con brócoli y media patata: 410 kcal y 39 g de proteína.\n\n" +
      "Te deja el día prácticamente clavado. Si te quedas corta, un yogur griego " +
      "te suma 10 g más sin apenas calorías.",
    cta: { icono: "plato", texto: "Ver recetas" },
  },

  p3: { autor: "usuario", texto: "¿Y mañana qué entreno?" },
  r3: {
    autor: "wayo",
    texto:
      "Mañana te toca tren superior, que llevas cuatro días sin tocarlo.\n\n" +
      "4×8 press banca · 4×10 remo con barra · 3×10 press militar · 3×12 curl. " +
      "Deja un par de repeticiones en recámara.",
    cta: { icono: "pesa", texto: "Ver entrenos" },
  },
};

const SUGERENCIAS = [
  { icono: "plato", texto: "¿Qué como con lo que llevo hoy?" },
  { icono: "barras", texto: "¿Qué me falta hoy para mis macros?" },
  { icono: "pesa", texto: "Dame una rutina para hoy" },
  { icono: "hoja", texto: "¿Cómo mejoro mi alimentación?" },
];

// Cada plano es un estado completo de la pantalla: así el componente se dibuja
// entero desde `plano` y no hay que deshacer nada al reiniciar.
// El rótulo va SIEMPRE pegado a lo que se ve en el chat en ese momento: si
// cambias un plano, cambia también su frase o dejan de contar lo mismo.
// `anclado` pega la conversación abajo, como el chat de la app cuando ya no
// cabe entera y se autodesplaza al último mensaje.
// `pantalla` monta encima una CAPTURA REAL de la app (no una réplica): es lo que
// pasa de verdad al pulsar el CTA de la respuesta. Entra deslizándose desde la
// derecha, como una navegación de iOS.
const CHAT_COMPLETO = ["saludo", "p1", "r1", "p2", "r2", "p3", "r3"];

const PLANOS = [
  { ms: 1800, rotulo: ["Con Wayo AI tienes un asistente ", "24/7", "."], mensajes: [] },
  { ms: 2800, rotulo: ["Te saluda y ya sabe ", "quién eres", "."], mensajes: ["saludo"] },
  { ms: 2400, rotulo: ["Ni hace falta saber ", "qué preguntar", "."], mensajes: ["saludo"], sugerencias: true },
  { ms: 1400, rotulo: ["Le preguntas ", "lo que quieras", "."], mensajes: ["saludo", "p1"] },
  { ms: 1600, rotulo: ["No busca en internet: ", "mira tu día", "."], mensajes: ["saludo", "p1"], pensando: true },
  { ms: 4000, rotulo: ["Te responde con ", "tus números de hoy", "."], mensajes: ["saludo", "p1", "r1"] },
  { ms: 1800, rotulo: ["Y sigues ", "la conversación", "."], mensajes: ["saludo", "p1", "r1", "p2"], pensando: true, anclado: true },
  { ms: 4000, rotulo: ["Te cuadra ", "la cena que te falta", "."], mensajes: ["saludo", "p1", "r1", "p2", "r2"], anclado: true },
  { ms: 3000, rotulo: ["Y te abre ", "el recetario", "."], mensajes: ["saludo", "p1", "r1", "p2", "r2"], anclado: true, pantalla: "recetario" },
  { ms: 1800, rotulo: ["No solo come: ", "también entrena", "."], mensajes: ["saludo", "p1", "r1", "p2", "r2", "p3"], pensando: true, anclado: true },
  { ms: 4200, rotulo: ["Te da ", "la rutina de mañana", "."], mensajes: CHAT_COMPLETO, anclado: true },
  { ms: 3000, rotulo: ["Con el entreno ", "ya montado", "."], mensajes: CHAT_COMPLETO, anclado: true, pantalla: "entrenamientos" },
  { ms: 2800, rotulo: ["ChatGPT no sabe ", "lo que has comido hoy", "."], mensajes: CHAT_COMPLETO, anclado: true },
];

const CAPTURAS = [
  { id: "recetario", alt: "Recetario de Wayo: recetas con su tiempo y sus calorías" },
  { id: "entrenamientos", alt: "Entrenamientos de Wayo: rutinas por objetivo, duración y nivel" },
];

const PLANO_FINAL = 10; // el que se enseña quieto si no hay animación
const DURACION = PLANOS.reduce((total, plano) => total + plano.ms, 0);

const prefiereMenosMovimiento = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Icono = ({ nombre, tamano = 20 }) => {
  const comun = {
    width: tamano,
    height: tamano,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "#3b82f6",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  if (nombre === "plato")
    return (
      <svg {...comun}>
        <path d="M6 3v8a2 2 0 004 0V3M8 11v10M17 3c-1.5 1.5-2 3.5-2 6s.5 3 2 3v9" />
      </svg>
    );
  if (nombre === "barras")
    return (
      <svg {...comun}>
        <path d="M5 20V10M12 20V4M19 20v-6" />
      </svg>
    );
  if (nombre === "pesa")
    return (
      <svg {...comun}>
        <path d="M3 9v6M6 7v10M18 7v10M21 9v6M6 12h12" />
      </svg>
    );
  return (
    <svg {...comun}>
      <path d="M20 4C10 4 4 9 4 16c0 2 1 4 1 4s6-1 10-5 5-11 5-11z" />
      <path d="M4 20c4-6 8-9 12-11" />
    </svg>
  );
};

const Chispa = () => (
  <span className="rl-chispa" aria-hidden="true">
    <svg width="13" height="13" viewBox="0 0 24 24" fill="#fff">
      <path d="M12 2l1.9 5.6L19.5 9.5 13.9 11.4 12 17l-1.9-5.6L4.5 9.5l5.6-1.9z" />
      <path d="M18.5 14l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9z" />
    </svg>
  </span>
);

export default function AsistenteReel() {
  const [plano, setPlano] = useState(null); // null = aún no ha arrancado
  const [ciclo, setCiclo] = useState(0); // remonta la barra de avance en cada vuelta
  const [activo, setActivo] = useState(false); // solo corre mientras se ve
  const [escala, setEscala] = useState(null);
  const platoRef = useRef(null);

  // La escala se mide en JS, no en CSS. Con unidades de contenedor bastaba una
  // pega de soporte para que `scale(var(--s))` quedase inválido y el móvil se
  // dibujase a tamaño real, gigante. Midiendo, el móvil SIEMPRE cabe.
  useEffect(() => {
    const nodo = platoRef.current;
    if (!nodo) return undefined;

    const medir = () => {
      const { width, height } = nodo.getBoundingClientRect();
      if (!width) return;

      // En 16:9 la caja tiene alto fijo y el móvil se ajusta a lo que sobre.
      // En vertical es al revés: la caja no tiene proporción fija y crece con
      // el móvil, así que la escala sale SOLO del ancho. Mirar el alto ahí
      // sería circular (el alto depende de la escala que estamos calculando).
      if (width >= 760) {
        if (!height) return;
        setEscala(Math.max(0.3, Math.min((height - 56) / 844, (width * 0.3) / 390)));
        return;
      }
      setEscala(Math.max(0.3, (width * 0.74) / 390));
    };

    medir();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", medir);
      return () => window.removeEventListener("resize", medir);
    }
    const observador = new ResizeObserver(medir);
    observador.observe(nodo);
    return () => observador.disconnect();
  }, []);

  // Corre solo mientras está en pantalla: fuera de vista no gasta nada.
  useEffect(() => {
    if (prefiereMenosMovimiento()) {
      setPlano(PLANO_FINAL);
      return undefined;
    }
    const nodo = platoRef.current;
    if (!nodo || typeof IntersectionObserver === "undefined") {
      setPlano(PLANO_FINAL);
      return undefined;
    }

    const observador = new IntersectionObserver(
      ([entrada]) => {
        setActivo(entrada.isIntersecting);
        if (entrada.isIntersecting) setPlano((actual) => (actual === null ? 0 : actual));
      },
      { threshold: 0.35 }
    );
    observador.observe(nodo);
    return () => observador.disconnect();
  }, []);

  // Un solo temporizador encadenado: al llegar al último plano vuelve al
  // primero, así el reel gira en bucle mientras se esté viendo.
  useEffect(() => {
    if (!activo || plano === null) return undefined;
    const espera = setTimeout(() => {
      const siguiente = (plano + 1) % PLANOS.length;
      if (siguiente === 0) setCiclo((n) => n + 1);
      setPlano(siguiente);
    }, PLANOS[plano].ms);
    return () => clearTimeout(espera);
  }, [plano, activo]);

  const actual = PLANOS[plano ?? PLANO_FINAL];
  const arrancado_ya = plano !== null;
  const visibles = arrancado_ya ? actual.mensajes : [];

  // Los rótulos giran como una rueda, así que en cada cambio conviven dos: el
  // que se va rodando hacia arriba y el que entra desde abajo. La pila guarda
  // como mucho esos dos.
  const [pila, setPila] = useState([]);
  useEffect(() => {
    if (plano === null) return;
    const entrante = PLANOS[plano].rotulo;
    if (!entrante) return;
    setPila((previa) => {
      const nuevo = { id: `${ciclo}-${plano}`, texto: entrante };
      return previa.length ? [previa[previa.length - 1], nuevo] : [nuevo];
    });
  }, [plano, ciclo]);

  return (
    <div className="rl">
      <div
        className="rl-escenario"
        ref={platoRef}
        style={escala ? { "--s": escala } : undefined}
      >
        <div className="rl-rotulo-col">
          <div className="rl-rueda">
            {pila.map((item, indice) => (
              <p
                key={item.id}
                className={
                  indice === pila.length - 1 ? "rl-rotulo rl-rotulo-entra" : "rl-rotulo rl-rotulo-sale"
                }
              >
                {/* Un ÚNICO hijo: el <p> es contenedor flex para centrar en la
                    ventana, y si el texto colgase suelto cada trozo sería un
                    elemento flex aparte y las palabras saldrían separadas. */}
                <span className="rl-rotulo-texto">
                  {item.texto[0]}
                  <em>{item.texto[1]}</em>
                  {item.texto[2]}
                </span>
              </p>
            ))}
          </div>
        </div>

        <div className="rl-movil-col">
          <div className="rl-movil">
            <div className="rl-screen">
              <div className="rl-notch" />
              <div className="rl-sbar">
                <span>20:41</span>
                <span className="rl-sbar-der">
                  <svg width="15" height="11" viewBox="0 0 16 12" fill="#fff" aria-hidden="true">
                    <path d="M8 11.2L.4 3.6a10.6 10.6 0 0115.2 0z" opacity=".95" />
                  </svg>
                  <svg width="24" height="12" viewBox="0 0 26 13" fill="none" aria-hidden="true">
                    <rect x=".7" y=".7" width="21" height="11.6" rx="3.2" stroke="#fff" strokeOpacity=".5" />
                    <rect x="2.4" y="2.4" width="17.6" height="8.2" rx="2" fill="#fff" />
                    <path d="M23.4 4.5v4a2.2 2.2 0 000-4z" fill="#fff" fillOpacity=".5" />
                  </svg>
                </span>
              </div>

              <div className="rl-head">
                <span className="rl-back" aria-hidden="true">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M11 18l-6-6 6-6" />
                  </svg>
                </span>
                <span className="rl-head-centro">
                  <span className="rl-avatar">
                    <img src="/assets/img/wayo-chat-avatar.png" alt="" width="34" height="34" loading="lazy" />
                  </span>
                  <span className="rl-head-textos">
                    <span className="rl-head-titulo">Asistente Wayo</span>
                    <span className="rl-head-sub">Tu entrenador y nutricionista personal</span>
                  </span>
                </span>
                <span className="rl-head-hueco" />
              </div>

              <div className={`rl-msgs${actual.anclado ? " rl-msgs-abajo" : ""}`}>
                {visibles.map((clave) => {
                  const mensaje = MENSAJES[clave];
                  return (
                    <div className="rl-fila rl-entra" key={clave}>
                      <div className={`rl-bub rl-${mensaje.autor === "wayo" ? "wayo" : "user"}`}>
                        {mensaje.autor === "wayo" && <Chispa />}
                        <p>{mensaje.texto}</p>
                      </div>

                      {mensaje.cta && (
                        <span className="rl-cta">
                          <Icono nombre={mensaje.cta.icono} tamano={15} />
                          {mensaje.cta.texto}
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                        </span>
                      )}
                    </div>
                  );
                })}

                {arrancado_ya && actual.sugerencias && (
                  <div className="rl-sugg rl-entra">
                    <p className="rl-sugg-lbl">Prueba a preguntarme:</p>
                    {SUGERENCIAS.map((s) => (
                      <div className="rl-sugg-item" key={s.texto}>
                        <Icono nombre={s.icono} />
                        <span>{s.texto}</span>
                      </div>
                    ))}
                  </div>
                )}

                {arrancado_ya && actual.pensando && (
                  <div className="rl-pensando rl-entra">
                    <span className="rl-spin" />
                    <span>Wayo está pensando...</span>
                  </div>
                )}
              </div>

              <div className="rl-input">
                <span className="rl-input-caja">Pregúntame lo que quieras...</span>
                <span className="rl-enviar" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
                    <path d="M3.4 20.4l17.5-7.5c.9-.4.9-1.6 0-2L3.4 3.6c-.7-.3-1.5.2-1.4 1L3 10.2l11 1.8L3 13.8l-1 5.6c-.1.8.7 1.3 1.4 1z" />
                  </svg>
                </span>
              </div>
              <span className="rl-homebar" />

              {/* Las dos capturas van SIEMPRE montadas (ocultas) para que el
                  navegador las descargue al entrar el reel en pantalla y no
                  haya un fotograma en blanco cuando toque enseñarlas. Traen su
                  propia barra de estado y su isla, así que tapan las nuestras. */}
              {CAPTURAS.map((captura) => (
                <img
                  key={captura.id}
                  className={`rl-captura${
                    arrancado_ya && actual.pantalla === captura.id ? " rl-captura-visible" : ""
                  }`}
                  src={`/assets/img/app-${captura.id}.jpg`}
                  alt={captura.alt}
                  loading="lazy"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Barra de avance: lo hace parecer un vídeo y avisa de que va a acabar. */}
        <div className="rl-barra" aria-hidden="true">
          <span
            key={ciclo}
            className="rl-barra-relleno"
            style={{
              animationDuration: `${DURACION}ms`,
              animationPlayState: activo && arrancado_ya ? "running" : "paused",
            }}
          />
        </div>
      </div>

      <p className="rl-pie">La pantalla del asistente tal cual está en la app. Datos de una usuaria de ejemplo.</p>
    </div>
  );
}
